import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import {
  isStorageAuthorityCommandsFeatureEnabled,
  isStorageAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { withUiDeadline } from "@/utils/uiDeadline";

const SCHEMA_VERSION = 1;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const RESOURCE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:@-]*$/;
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/;
const MAX_SIGNED_URL_LENGTH = 8192;
const MAX_ASSET_BYTES = 100 * 1024 * 1024;

const RESOURCE_PROFILES = Object.freeze({
  "profile-avatar": Object.freeze({
    audiences: new Set(["private", "authenticated", "public"]),
    maximumBytes: 10 * 1024 * 1024,
    mimeTypes: new Set(["image/avif", "image/jpeg", "image/png", "image/webp"]),
  }),
  "organisation-logo": Object.freeze({
    audiences: new Set(["private", "members", "public"]),
    maximumBytes: 10 * 1024 * 1024,
    mimeTypes: new Set(["image/avif", "image/jpeg", "image/png", "image/webp"]),
  }),
  "item-media": Object.freeze({
    audiences: new Set(["private", "members", "public"]),
    maximumBytes: MAX_ASSET_BYTES,
    mimeTypes: new Set([
      "application/pdf",
      "audio/mpeg",
      "audio/ogg",
      "audio/wav",
      "image/avif",
      "image/gif",
      "image/jpeg",
      "image/png",
      "image/webp",
      "video/mp4",
      "video/webm",
    ]),
  }),
  "media-post": Object.freeze({
    audiences: new Set(["private", "members", "public"]),
    maximumBytes: MAX_ASSET_BYTES,
    mimeTypes: new Set([
      "application/pdf", "audio/mpeg", "audio/ogg", "audio/wav",
      "image/avif", "image/gif", "image/jpeg", "image/png", "image/webp",
      "video/mp4", "video/webm",
    ]),
  }),
  "message-attachment": Object.freeze({
    audiences: new Set(["participants"]),
    maximumBytes: 25 * 1024 * 1024,
    mimeTypes: new Set([
      "application/pdf",
      "audio/mpeg",
      "audio/ogg",
      "image/avif",
      "image/gif",
      "image/jpeg",
      "image/png",
      "image/webp",
      "video/mp4",
      "video/webm",
    ]),
  }),
});
const ALL_MIME_TYPES = new Set(
  Object.values(RESOURCE_PROFILES).flatMap((profile) => [...profile.mimeTypes]),
);
const UPLOAD_HEADER_KEYS = Object.freeze([
  "Content-Type",
  "x-goog-if-generation-match",
  "x-goog-meta-sha256",
  "x-goog-meta-upload-id",
]);

export class StorageAuthorityServiceError extends Error {
  constructor(code = "unknown", message = "Assets are unavailable.") {
    super(message);
    this.name = "StorageAuthorityServiceError";
    this.code = code;
  }
}

function fail(code, message = "Assets are unavailable.") {
  throw new StorageAuthorityServiceError(code, message);
}

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactKeys(value, expectedKeys) {
  if (!isPlainRecord(value)) return false;
  const ownKeys = Reflect.ownKeys(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const actualKeys = ownKeys.filter((key) => typeof key === "string").sort();
  const sortedExpectedKeys = [...expectedKeys].sort();
  return (
    ownKeys.every(
      (key) =>
        typeof key === "string" &&
        descriptors[key]?.enumerable === true &&
        Object.prototype.hasOwnProperty.call(descriptors[key], "value"),
    ) &&
    actualKeys.length === sortedExpectedKeys.length &&
    actualKeys.every((key, index) => key === sortedExpectedKeys[index])
  );
}

function requireExactKeys(value, expectedKeys, code = "invalid-argument") {
  if (!hasExactKeys(value, expectedKeys)) fail(code);
  return value;
}

function normalizeOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument", "The asset operation is invalid.");
  }
  return value;
}

function normalizeRevision(value, { creation = false } = {}) {
  if (
    !Number.isSafeInteger(value) ||
    value < 0 ||
    (creation && value !== 0)
  ) {
    fail("invalid-argument", "The asset revision is invalid.");
  }
  return value;
}

function normalizeSha256(value, code = "invalid-argument") {
  if (typeof value !== "string" || !SHA256_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeResource(value) {
  requireExactKeys(value, ["kind", "resourceId"]);
  const profile = RESOURCE_PROFILES[value.kind];
  if (
    !profile ||
    typeof value.resourceId !== "string" ||
    value.resourceId.length > 128 ||
    !RESOURCE_ID_PATTERN.test(value.resourceId)
  ) {
    fail("invalid-argument", "The asset resource is invalid.");
  }
  if (value.kind === "profile-avatar" && value.resourceId !== "self") {
    fail("invalid-argument", "The asset resource is invalid.");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    kind: value.kind,
    resourceId: value.resourceId,
  });
}

function normalizeFile(value, resourceKind) {
  requireExactKeys(value, ["mimeType", "sizeBytes", "sha256"]);
  const profile = RESOURCE_PROFILES[resourceKind];
  if (
    !profile.mimeTypes.has(value.mimeType) ||
    !Number.isSafeInteger(value.sizeBytes) ||
    value.sizeBytes < 1 ||
    value.sizeBytes > profile.maximumBytes
  ) {
    fail("invalid-argument", "The asset file is invalid.");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    mimeType: value.mimeType,
    sizeBytes: value.sizeBytes,
    sha256: normalizeSha256(value.sha256),
  });
}

export function normalizeCreateUploadIntent(value) {
  requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "resource",
    "audience",
    "file",
  ]);
  const resource = normalizeResource(value.resource);
  const profile = RESOURCE_PROFILES[resource.kind];
  if (!profile.audiences.has(value.audience)) {
    fail("invalid-argument", "The asset audience is invalid.");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision, { creation: true }),
    resource,
    audience: value.audience,
    file: normalizeFile(value.file, resource.kind),
  });
}

export function normalizeFinalizeUploadIntent(value) {
  requireExactKeys(value, ["operationId", "expectedRevision", "uploadId"]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    expectedRevision: normalizeRevision(value.expectedRevision),
    uploadId: normalizeSha256(value.uploadId),
  });
}

function normalizeSignedUrl(value, code = "invalid-response") {
  if (
    typeof value !== "string" ||
    value.length < 1 ||
    value.length > MAX_SIGNED_URL_LENGTH ||
    value !== value.trim() ||
    CONTROL_CHARACTERS.test(value)
  ) {
    fail(code);
  }
  try {
    const parsed = new URL(value);
    if (
      parsed.protocol !== "https:" ||
      !parsed.hostname ||
      parsed.username ||
      parsed.password ||
      parsed.hash
    ) {
      fail(code);
    }
  } catch (error) {
    if (error instanceof StorageAuthorityServiceError) throw error;
    fail(code);
  }
  return value;
}

function normalizeTimestamp(value) {
  if (!Number.isSafeInteger(value) || value < 1) fail("invalid-response");
  return value;
}

function normalizeAssetMetadata({ mimeType, sizeBytes }, code = "invalid-response") {
  if (
    !ALL_MIME_TYPES.has(mimeType) ||
    !Number.isSafeInteger(sizeBytes) ||
    sizeBytes < 1 ||
    sizeBytes > MAX_ASSET_BYTES
  ) {
    fail(code);
  }
  return Object.freeze({ mimeType, sizeBytes });
}

function normalizeUploadHeaders(value, { request, uploadId }) {
  if (
    !hasExactKeys(value, UPLOAD_HEADER_KEYS) ||
    value["Content-Type"] !== request.file.mimeType ||
    value["x-goog-if-generation-match"] !== "0" ||
    value["x-goog-meta-upload-id"] !== uploadId ||
    value["x-goog-meta-sha256"] !== request.file.sha256
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    "Content-Type": value["Content-Type"],
    "x-goog-if-generation-match": "0",
    "x-goog-meta-sha256": value["x-goog-meta-sha256"],
    "x-goog-meta-upload-id": value["x-goog-meta-upload-id"],
  });
}

function normalizeUploadIntentResponse(value, request) {
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "uploadId",
      "outcome",
      "method",
      "uploadUrl",
      "expiresAt",
      "requiredHeaders",
    ],
    "invalid-response",
  );
  const uploadId = normalizeSha256(value.uploadId, "invalid-response");
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    !new Set(["created", "replayed"]).has(value.outcome) ||
    value.method !== "PUT"
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    uploadId,
    outcome: value.outcome,
    method: "PUT",
    uploadUrl: normalizeSignedUrl(value.uploadUrl),
    expiresAt: normalizeTimestamp(value.expiresAt),
    requiredHeaders: normalizeUploadHeaders(value.requiredHeaders, {
      request,
      uploadId,
    }),
  });
}

function normalizeFinalizeResponse(value, request) {
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "assetId",
      "mimeType",
      "sizeBytes",
      "state",
      "revision",
      "outcome",
    ],
    "invalid-response",
  );
  const metadata = normalizeAssetMetadata(value);
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.assetId !== request.uploadId ||
    value.state !== "finalized" ||
    !new Set(["finalized", "replayed"]).has(value.outcome) ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 1
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    assetId: value.assetId,
    mimeType: metadata.mimeType,
    sizeBytes: metadata.sizeBytes,
    state: "finalized",
    revision: value.revision,
    outcome: value.outcome,
  });
}

function normalizeDownloadResponse(value, assetId) {
  requireExactKeys(
    value,
    ["schemaVersion", "assetId", "mimeType", "sizeBytes", "downloadUrl", "expiresAt"],
    "invalid-response",
  );
  const metadata = normalizeAssetMetadata(value);
  if (value.schemaVersion !== SCHEMA_VERSION || value.assetId !== assetId) {
    fail("invalid-response");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    assetId,
    mimeType: metadata.mimeType,
    sizeBytes: metadata.sizeBytes,
    downloadUrl: normalizeSignedUrl(value.downloadUrl),
    expiresAt: normalizeTimestamp(value.expiresAt),
  });
}

function normalizeServiceError(error) {
  if (error instanceof StorageAuthorityServiceError) return error;
  const messages = {
    aborted: "This asset changed elsewhere. Load the latest version and try again.",
    "deadline-exceeded": "The asset service did not respond in time.",
    "failed-precondition": "The asset could not be verified.",
    "invalid-argument": "The asset request was invalid.",
    "not-found": "The asset is unavailable.",
    "permission-denied": "You do not have access to this asset.",
    "stale-auth-context": "The signed-in account changed during the asset operation.",
    "ui-deadline": "The asset service did not respond in time.",
    unauthenticated: "Sign in again to continue.",
    unavailable: "The asset service is temporarily unavailable.",
  };
  const candidateCode = String(error?.code || "unknown").split("/").pop();
  const code = Object.hasOwn(messages, candidateCode) ? candidateCode : "unknown";
  return new StorageAuthorityServiceError(code, messages[code]);
}

function currentUid(authInstance) {
  const uid = authInstance?.currentUser?.uid;
  if (uid === null || uid === undefined) return null;
  if (
    typeof uid !== "string" ||
    uid.length < 1 ||
    uid.length > 128 ||
    CONTROL_CHARACTERS.test(uid)
  ) {
    fail("unauthenticated", "Sign in again to continue.");
  }
  return uid;
}

export function createStorageAuthorityOperationId(
  existingOperationId = null,
  cryptoProvider = globalThis.crypto,
) {
  if (existingOperationId !== null) return normalizeOperationId(existingOperationId);
  if (typeof cryptoProvider?.randomUUID === "function") {
    const candidate = cryptoProvider.randomUUID().replaceAll("-", "_");
    if (OPERATION_ID_PATTERN.test(candidate)) return candidate;
  }
  if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    return `storage_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("")}`;
  }
  fail(
    "failed-precondition",
    "Secure asset operations are unavailable.",
  );
}

export async function describeStorageAuthorityFile(
  file,
  resourceKind,
  cryptoProvider = globalThis.crypto,
) {
  const profile = RESOURCE_PROFILES[resourceKind];
  if (
    !profile ||
    !file ||
    typeof file !== "object" ||
    typeof file.arrayBuffer !== "function" ||
    !Number.isSafeInteger(file.size) ||
    file.size < 1 ||
    file.size > profile.maximumBytes ||
    !profile.mimeTypes.has(file.type)
  ) {
    fail("invalid-argument", "The selected asset is invalid.");
  }

  const expectedSize = file.size;
  const expectedMimeType = file.type;
  let contents;
  try {
    contents = await file.arrayBuffer();
  } catch (_error) {
    fail("invalid-argument", "The selected asset could not be read.");
  }
  if (
    !(contents instanceof ArrayBuffer) ||
    contents.byteLength !== expectedSize ||
    file.size !== expectedSize ||
    file.type !== expectedMimeType
  ) {
    fail("invalid-argument", "The selected asset changed while it was being read.");
  }
  if (typeof cryptoProvider?.subtle?.digest !== "function") {
    fail("failed-precondition", "Secure asset verification is unavailable.");
  }

  let digest;
  try {
    digest = await cryptoProvider.subtle.digest("SHA-256", contents);
  } catch (_error) {
    fail("failed-precondition", "Secure asset verification is unavailable.");
  }
  if (!(digest instanceof ArrayBuffer) || digest.byteLength !== 32) {
    fail("failed-precondition", "Secure asset verification is unavailable.");
  }

  return Object.freeze({
    mimeType: expectedMimeType,
    sizeBytes: expectedSize,
    sha256: Array.from(new Uint8Array(digest), (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join(""),
  });
}

export function createStorageAuthorityService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  fetchImplementation = globalThis.fetch,
  clock = Date.now,
  readsEnabled = isStorageAuthorityReadsFeatureEnabled,
  commandsEnabled = isStorageAuthorityCommandsFeatureEnabled,
} = {}) {
  const callables = new Map();
  // A signed upload URL is a short-lived bearer capability. Keep provenance
  // in-memory and object-identity-bound so a caller cannot hand this transport
  // an arbitrary HTTPS destination with lookalike fields.
  const issuedUploadGrants = new WeakMap();
  const callableFor = (name) => {
    if (!callables.has(name)) {
      callables.set(name, callableFactory(functionsInstance, name));
    }
    return callables.get(name);
  };

  const invoke = async (name, request, { command = false } = {}) => {
    if (command ? !commandsEnabled : !readsEnabled) fail("feature-disabled");
    const expectedUid = currentUid(authInstance);
    if (command && expectedUid === null) fail("unauthenticated");
    try {
      const response = await withUiDeadline(callableFor(name)(request), {
        phase: command ? "updating asset" : "loading asset",
        timeoutMs: command ? 30_000 : 15_000,
      });
      if (currentUid(authInstance) !== expectedUid) fail("stale-auth-context");
      return response?.data;
    } catch (error) {
      throw normalizeServiceError(error);
    }
  };

  return Object.freeze({
    async createUploadIntent(intentValue) {
      const request = normalizeCreateUploadIntent(intentValue);
      const expectedUid = currentUid(authInstance);
      if (expectedUid === null) fail("unauthenticated");
      const value = await invoke("createUploadIntent", request, { command: true });
      const grant = normalizeUploadIntentResponse(value, request);
      issuedUploadGrants.set(grant, Object.freeze({
        uid: expectedUid,
        file: request.file,
      }));
      return grant;
    },

    async putUploadFile(grant, file) {
      if (!commandsEnabled) fail("feature-disabled");
      const provenance = issuedUploadGrants.get(grant);
      if (!provenance) {
        fail("invalid-argument", "The asset upload grant is invalid.");
      }
      const expectedUid = currentUid(authInstance);
      if (expectedUid === null) fail("unauthenticated");
      if (expectedUid !== provenance.uid) fail("stale-auth-context");
      if (
        !file ||
        typeof file !== "object" ||
        !Number.isSafeInteger(file.size) ||
        file.size !== provenance.file.sizeBytes ||
        file.type !== provenance.file.mimeType
      ) {
        fail("invalid-argument", "The selected asset does not match its upload intent.");
      }
      const now = typeof clock === "function" ? clock() : NaN;
      if (!Number.isSafeInteger(now) || now < 0) {
        fail("failed-precondition", "The asset upload clock is unavailable.");
      }
      if (now >= grant.expiresAt) {
        fail("failed-precondition", "The asset upload grant has expired.");
      }
      if (typeof fetchImplementation !== "function") {
        fail("failed-precondition", "The asset upload transport is unavailable.");
      }

      let response;
      try {
        response = await fetchImplementation(grant.uploadUrl, {
          method: "PUT",
          headers: { ...grant.requiredHeaders },
          body: file,
          cache: "no-store",
          credentials: "omit",
          keepalive: false,
          mode: "cors",
          redirect: "error",
          referrerPolicy: "no-referrer",
        });
      } catch (_error) {
        throw new StorageAuthorityServiceError(
          "unavailable",
          "The asset could not be uploaded.",
        );
      }
      if (currentUid(authInstance) !== expectedUid) fail("stale-auth-context");
      const status = response?.status;
      if (!Number.isSafeInteger(status) || status < 100 || status > 599) {
        fail("invalid-response", "The asset upload response was invalid.");
      }
      if (status !== 412 && response?.ok !== true) {
        throw new StorageAuthorityServiceError(
          "unavailable",
          "The asset could not be uploaded.",
        );
      }
      return Object.freeze({
        schemaVersion: SCHEMA_VERSION,
        uploadId: grant.uploadId,
        outcome: status === 412 ? "already-uploaded" : "uploaded",
        status,
      });
    },

    async finalizeUpload(intentValue) {
      const request = normalizeFinalizeUploadIntent(intentValue);
      const value = await invoke("finalizeUpload", request, { command: true });
      return normalizeFinalizeResponse(value, request);
    },

    async createAssetDownloadUrl(assetIdValue) {
      const assetId = normalizeSha256(assetIdValue);
      const value = await invoke("createAssetDownloadUrl", {
        schemaVersion: SCHEMA_VERSION,
        assetId,
      });
      return normalizeDownloadResponse(value, assetId);
    },
  });
}

export const storageAuthorityService = createStorageAuthorityService();
